import { useEffect, useState, useCallback } from 'react';
import { authAPI } from '../../services/auth';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            element: HTMLElement,
            options: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              type?: 'standard' | 'icon';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              width?: number;
              locale?: string;
            }
          ) => void;
          prompt: () => void;
        };
      };
    };
  }
}

interface GoogleLoginButtonProps {
  onSuccess: (idToken: string) => Promise<void>;
  onError?: (error: string) => void;
  text?: 'signin_with' | 'signup_with' | 'continue_with';
}

export default function GoogleLoginButton({
  onSuccess,
  onError,
  text = 'continue_with',
}: GoogleLoginButtonProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);

  // Check if Google OAuth is configured
  useEffect(() => {
    const checkConfig = async () => {
      try {
        const config = await authAPI.getGoogleConfig();
        setIsEnabled(config.enabled);
        setClientId(config.clientId);
      } catch {
        setIsEnabled(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkConfig();
  }, []);

  const handleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      try {
        await onSuccess(response.credential);
      } catch (error) {
        onError?.((error as Error).message);
      }
    },
    [onSuccess, onError]
  );

  // Load and initialize Google Identity Services
  useEffect(() => {
    if (!isEnabled || !clientId) return;

    const initializeGoogle = () => {
      if (!window.google) return;

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      const buttonDiv = document.getElementById('google-login-button');
      if (buttonDiv) {
        window.google.accounts.id.renderButton(buttonDiv, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          shape: 'rectangular',
          text: text,
          width: 280,
          locale: 'pl',
        });
      }
    };

    // Check if script is already loaded
    if (window.google) {
      initializeGoogle();
      return;
    }

    // Load the Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogle;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector(
        'script[src="https://accounts.google.com/gsi/client"]'
      );
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [isEnabled, clientId, handleCredentialResponse, text]);

  if (isLoading) {
    return (
      <div className="h-10 bg-slate-100 dark:bg-slate-700 rounded-lg animate-pulse" />
    );
  }

  if (!isEnabled) {
    return null;
  }

  return (
    <div className="flex justify-center">
      <div id="google-login-button" />
    </div>
  );
}
